import { Button } from '@/components/ui/button'
import { DialogHeader } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Loader2, Mail } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Divider } from './divider'
import { ErrorMessage } from './error-message'
import { PrimaryButton } from './primary-button'
import { StepHeader } from './step-header'
import { StepTitleSection } from './step-title-section'
import { WalletButton } from './wallet-button'

function GoogleIcon({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
				fill="#4285F4"
			/>
			<path
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
				fill="#34A853"
			/>
			<path
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
				fill="#FBBC05"
			/>
			<path
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
				fill="#EA4335"
			/>
		</svg>
	)
}

type EmailStepProps = {
	email: string
	setEmail: (value: string) => void
	onSubmit: () => void
	onBack?: () => void
	isLoading: boolean
	error: string | null
	// Google auth props
	onGoogleLogin: () => void
	isGoogleLoading: boolean
	// Wallet auth props
	onWalletConnect: () => void
	isWalletPending: boolean
	walletStep: 'idle' | 'connecting' | 'signing' | 'verifying'
	walletError: string | null
}

export function EmailStep({
	email,
	setEmail,
	onSubmit,
	onBack,
	isLoading,
	error,
	onGoogleLogin,
	isGoogleLoading,
	onWalletConnect,
	isWalletPending,
	walletStep,
	walletError,
}: EmailStepProps) {
	const t = useTranslations('auth')
	const displayError = error || walletError
	const isAnyLoading = isLoading || isGoogleLoading || isWalletPending

	return (
		<>
			<DialogHeader>
				<div className="flex flex-col gap-[32px]">
					<StepHeader showBack={!!onBack} onBack={onBack} />
					<StepTitleSection title={t('logInToChance')} description={t('enterEmailDescription')} />
				</div>
			</DialogHeader>

			<div className="flex flex-col gap-[24px]">
				<div className="relative">
					<Mail className="-translate-y-1/2 absolute top-[25px] left-3 size-[20px] text-brand-gray-secondary" />
					<Input
						type="email"
						placeholder={t('emailPlaceholder')}
						value={email}
						onChange={e => setEmail(e.target.value)}
						className="h-[48px] rounded-[12px] border border-brand-gray bg-brand-dark py-[12px] pl-[37px] font-normal text-[16px] text-brand-gray-secondary leading-[100%] placeholder:text-brand-gray-secondary"
						autoFocus
						onKeyDown={e => e.key === 'Enter' && onSubmit()}
						disabled={isAnyLoading}
					/>
				</div>

				<PrimaryButton onClick={onSubmit} disabled={isAnyLoading || !email} isLoading={isLoading}>
					{t('continueWithEmail')}
				</PrimaryButton>

				<Divider />

				<Button
					onClick={onGoogleLogin}
					disabled={isAnyLoading}
					className="min-h-[48px] w-full rounded-[12px] border border-brand-gray bg-transparent py-[15px] font-medium text-[16px] text-white leading-[100%] hover:bg-brand-gray/20"
				>
					{isGoogleLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							{t('oauthRedirect')}
						</>
					) : (
						<>
							<GoogleIcon className="mr-2 size-[20px]" />
							{t('continueWithGoogle')}
						</>
					)}
				</Button>

				<WalletButton
					onClick={onWalletConnect}
					isPending={isWalletPending}
					step={walletStep}
					disabled={isAnyLoading}
				/>

				<ErrorMessage error={displayError} />
			</div>
		</>
	)
}
